/**
 * Marlow Product Configurator
 * Based on Flint pattern - visual variant selector
 */

class MarlowConfigurator {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.section = document.querySelector(`.marlow-configurator[data-section-id="${sectionId}"]`);
    
    if (!this.section) {
      console.warn(`[Configurator] Section ${sectionId} not found`);
      return;
    }
    
    this.loadData();
    this.init();
  }
  
  loadData() {
    const dataEl = document.querySelector(`[data-configurator-data-${this.sectionId}]`);
    if (!dataEl) {
      console.error('[Configurator] Data not found');
      return;
    }
    
    try {
      const data = JSON.parse(dataEl.textContent);
      this.product = data.product;
      this.variants = data.variants;
      this.options = data.options;
      this.settings = data.settings;
      this.basePrice = this.settings.basePrice || 0;
    } catch (e) {
      console.error('[Configurator] Failed to parse data:', e);
    }
  }
  
  init() {
    this.selections = {};
    
    this.cacheElements();
    this.bindEvents();
    this.initializeDefaults();
    this.updateDisplay();
    
    console.log('[Configurator] Initialized', {
      product: this.product?.title,
      variants: this.variants?.length,
      options: this.options
    });
  }
  
  cacheElements() {
    this.swatches = this.section.querySelectorAll('.marlow-configurator__swatch');
    this.summaryEl = this.section.querySelector('[data-configurator-summary]');
    
    // Quantity and add to cart
    this.qtyInput = this.section.querySelector('[data-configurator-quantity]');
    this.qtyMinusBtn = this.section.querySelector('[data-qty-minus]');
    this.qtyPlusBtn = this.section.querySelector('[data-qty-plus]');
    this.addToCartBtn = this.section.querySelector('[data-configurator-add]');
    
    // Find the main product form and variant input
    this.productForm = document.querySelector('form[action*="/cart/add"]');
    this.variantInput = this.productForm?.querySelector('input[name="id"], select[name="id"]');
  }
  
  bindEvents() {
    this.swatches.forEach(swatch => {
      swatch.addEventListener('click', (e) => this.handleSwatchClick(e));
    });
    
    // Quantity controls
    if (this.qtyMinusBtn) {
      this.qtyMinusBtn.addEventListener('click', () => this.updateQuantity(-1));
    }
    if (this.qtyPlusBtn) {
      this.qtyPlusBtn.addEventListener('click', () => this.updateQuantity(1));
    }
    
    // Add to cart
    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', (e) => this.handleAddToCart(e));
    }
  }
  
  updateQuantity(change) {
    if (!this.qtyInput) return;
    
    let currentQty = parseInt(this.qtyInput.value) || 1;
    let newQty = currentQty + change;
    
    if (newQty < 1) newQty = 1;
    this.qtyInput.value = newQty;
    
    // Also update main form quantity if it exists
    if (this.productForm) {
      const mainQtyInput = this.productForm.querySelector('input[name="quantity"]');
      if (mainQtyInput) {
        mainQtyInput.value = newQty;
      }
    }
  }
  
  handleAddToCart(e) {
    e.preventDefault();
    
    const variant = this.findMatchingVariant();
    if (!variant) {
      console.warn('[Configurator] No variant selected');
      return;
    }
    
    const quantity = parseInt(this.qtyInput?.value) || 1;
    
    // If there's a main product form, submit it
    if (this.productForm) {
      // Update the form with our selected variant and quantity
      if (this.variantInput) {
        this.variantInput.value = variant.id;
      }
      const mainQtyInput = this.productForm.querySelector('input[name="quantity"]');
      if (mainQtyInput) {
        mainQtyInput.value = quantity;
      }
      
      // Submit the form
      this.productForm.dispatchEvent(new Event('submit', { bubbles: true }));
      this.productForm.submit();
    } else {
      // Fallback: use AJAX to add to cart
      this.addToCartAjax(variant.id, quantity);
    }
  }
  
  addToCartAjax(variantId, quantity) {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: quantity
        }]
      })
    })
    .then(response => response.json())
    .then(data => {
      console.log('[Configurator] Added to cart:', data);
      // Trigger cart update event
      document.dispatchEvent(new CustomEvent('cart:updated'));
      // Show success feedback
      this.showAddToCartFeedback();
    })
    .catch(error => {
      console.error('[Configurator] Error adding to cart:', error);
    });
  }
  
  showAddToCartFeedback() {
    if (!this.addToCartBtn) return;
    
    const originalText = this.addToCartBtn.textContent;
    this.addToCartBtn.textContent = 'Added!';
    this.addToCartBtn.style.background = '#2d5a27';
    
    setTimeout(() => {
      this.addToCartBtn.textContent = originalText;
      this.addToCartBtn.style.background = '';
    }, 2000);
  }
  
  handleSwatchClick(e) {
    const swatch = e.currentTarget;
    const value = swatch.dataset.value;
    const position = parseInt(swatch.dataset.optionPosition);
    
    // Update selection
    this.selections[position] = value;
    
    // Update UI
    this.updateSwatchStates(position, value);
    
    // Find and update variant
    this.updateVariant();
    
    // Update display
    this.updateDisplay();
  }
  
  updateSwatchStates(position, selectedValue) {
    const groupSwatches = this.section.querySelectorAll(`[data-option-position="${position}"]`);
    
    groupSwatches.forEach(swatch => {
      const isSelected = swatch.dataset.value === selectedValue;
      swatch.classList.toggle('is-selected', isSelected);
    });
  }
  
  findMatchingVariant() {
    if (!this.variants || !this.selections) return null;
    
    return this.variants.find(variant => {
      return variant.option1 === this.selections[1] &&
             (variant.option2 === this.selections[2] || !this.selections[2]) &&
             (variant.option3 === this.selections[3] || !this.selections[3]);
    });
  }
  
  updateVariant() {
    const variant = this.findMatchingVariant();
    
    if (!variant) {
      console.warn('[Configurator] No matching variant found', this.selections);
      return;
    }
    
    // Update URL with variant ID — triggers theme price update
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url.toString());
    
    // Dispatch native variant change event for Horizon theme
    window.dispatchEvent(new Event('popstate'));
    
    // Try to sync native variant select
    const nativeSelect = document.querySelector('select[name="id"]');
    if (nativeSelect && nativeSelect.value !== String(variant.id)) {
      nativeSelect.value = variant.id;
      nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
      nativeSelect.dispatchEvent(new Event('input', { bubbles: true }));
    }
    
    // Fetch updated section HTML and dispatch Horizon variant:update event
    this.updatePriceDisplay(variant);
    
    // Update hidden variant input
    if (this.variantInput) {
      this.variantInput.value = variant.id;
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    
    // Trigger variant change event for other components
    document.dispatchEvent(new CustomEvent('marlow:variantChanged', {
      detail: {
        variant: variant,
        selections: this.selections
      }
    }));
  }
  
  updatePriceDisplay(variant) {
    // Update Marlow hero price
    const heroPrice = document.querySelector('.marlow-product-hero__price');
    if (heroPrice && variant.price != null) {
      heroPrice.textContent = this.formatMoney(variant.price);
    }
    
    // Update configurator inline price with delta
    const configPrice = this.section.querySelector('[data-configurator-price]');
    if (configPrice && variant.price != null) {
      let priceHtml = '';
      
      // Calculate price delta from base price
      const delta = variant.price - this.basePrice;
      const deltaHtml = this.formatPriceDelta(delta);
      
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceHtml = `<span class="marlow-configurator__price--sale">${this.formatMoney(variant.price)}</span>`;
        priceHtml += `<span class="marlow-configurator__price--compare">${this.formatMoney(variant.compare_at_price)}</span>`;
        if (this.settings.showPriceDelta) {
          priceHtml += deltaHtml;
        }
      } else {
        priceHtml = `<span class="marlow-configurator__price--current">${this.formatMoney(variant.price)}</span>`;
        if (this.settings.showPriceDelta) {
          priceHtml += deltaHtml;
        }
      }
      configPrice.innerHTML = priceHtml;
      console.log('[Configurator] Price updated to', this.formatMoney(variant.price), 'Delta:', delta);
    }
    
    // Update compare-at price in hero if it exists
    const comparePrice = document.querySelector('.marlow-product-hero__compare-price');
    if (comparePrice) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        comparePrice.textContent = this.formatMoney(variant.compare_at_price);
        comparePrice.style.display = '';
      } else {
        comparePrice.style.display = 'none';
      }
    }
  }
  
  formatPriceDelta(delta) {
    if (delta === 0) return ''; // No delta for base price
    
    const absDelta = Math.abs(delta);
    const sign = delta > 0 ? '+' : '-';
    const formatted = this.formatMoney(absDelta);
    const modifierClass = delta > 0 ? 'is-increase' : 'is-decrease';
    
    return `<span class="marlow-configurator__price-delta ${modifierClass}">${sign}${formatted}</span>`;
  }
  
  formatMoney(priceInPence) {
    const amount = (priceInPence / 100).toFixed(2);
    return '£' + amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  
  updateDisplay() {
    if (!this.summaryEl) return;
    
    const selectedTexts = [];
    Object.keys(this.selections).sort().forEach(key => {
      if (this.selections[key]) {
        selectedTexts.push(this.selections[key]);
      }
    });
    
    if (selectedTexts.length > 0) {
      this.summaryEl.textContent = selectedTexts.join(' + ');
    } else {
      this.summaryEl.textContent = '—';
    }
  }
  
  initializeDefaults() {
    // Select first option of each group by default
    const groups = this.section.querySelectorAll('.marlow-configurator__group');
    
    groups.forEach((group, index) => {
      const position = index + 1;
      const firstSwatch = group.querySelector('.marlow-configurator__swatch');
      
      if (firstSwatch) {
        const value = firstSwatch.dataset.value;
        
        this.selections[position] = value;
        this.updateSwatchStates(position, value);
      }
    });
    
    // Set initial variant
    this.updateVariant();
  }
}

// Initialize when DOM is ready
function initConfigurators() {
  const configurators = document.querySelectorAll('.marlow-configurator');
  configurators.forEach(section => {
    const sectionId = section.dataset.sectionId;
    if (sectionId) {
      new MarlowConfigurator(sectionId);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initConfigurators);
} else {
  initConfigurators();
}

// Reinitialize on Shopify section render
document.addEventListener('shopify:section:load', (e) => {
  const sectionId = e.detail.sectionId;
  if (sectionId) {
    setTimeout(() => new MarlowConfigurator(sectionId), 100);
  }
});
